// src/repositories/supabase/SupabaseAgentProposalRepository.ts
//
// Propuestas de los agentes (tabla agent_proposals). Reglas:
//  - firm_id sale SIEMPRE de la sesión (requireAuth → auth.uid()); ningún método lo recibe.
//  - Toda lectura filtra por firm_id + company_id (+ periodo) y lista columnas explícitas.
//  - Los cambios de estado pasan solo por las funciones agent_proposal_* (SECURITY DEFINER), que
//    también escriben la auditoría de forma atómica. Este repositorio no hace UPDATE directo.
import type { SupabaseClient } from '@supabase/supabase-js';
import type { IAgentProposalRepository } from '../types';
import { PropuestaDuplicadaError } from '../types';
import {
  AGENTES_PROPUESTA,
  ESTADOS_PROPUESTA,
  type AgentProposal,
  type AgentePropuesta,
  type EstadoPropuesta,
  type JsonObject,
  type NuevaPropuesta,
} from '../../agents/proposals/types';
import { requireAuth } from '../../services/supabaseClient';
import { assertCompanyId } from '../../utils/assertFirmId';

const TABLE = 'agent_proposals';

const COLS = [
  'id', 'firm_id', 'company_id', 'periodo', 'agente', 'tipo', 'entidad_id',
  'propuesta', 'valor_final', 'confianza', 'explicacion', 'estado', 'creado_por_agente',
  'revisado_por', 'revisado_en', 'aplicada_en', 'error', 'creado_en', 'expira_en',
].join(', ');

/** Código de Postgres para violación de índice único (propuesta pendiente duplicada). */
const PG_UNIQUE_VIOLATION = '23505';

export function mapRow(row: Record<string, unknown>): AgentProposal {
  const agente = row.agente as string;
  const estado = row.estado as string;
  if (!(AGENTES_PROPUESTA as readonly string[]).includes(agente)) {
    throw new Error(`[AgentProposal] agente desconocido en la base de datos: ${agente}`);
  }
  if (!(ESTADOS_PROPUESTA as readonly string[]).includes(estado)) {
    throw new Error(`[AgentProposal] estado desconocido en la base de datos: ${estado}`);
  }
  return {
    id:                row.id as string,
    firm_id:           row.firm_id as string,
    company_id:        row.company_id as string,
    periodo:           row.periodo as string,
    agente:            agente as AgentePropuesta,
    tipo:              row.tipo as string,
    entidad_id:        row.entidad_id as string,
    propuesta:         (row.propuesta ?? {}) as JsonObject,
    valor_final:       (row.valor_final as JsonObject | null) ?? null,
    confianza:         Number(row.confianza),
    explicacion:       row.explicacion as string,
    estado:            estado as EstadoPropuesta,
    creado_por_agente: row.creado_por_agente as string,
    revisado_por:      (row.revisado_por as string | null) ?? null,
    revisado_en:       (row.revisado_en as string | null) ?? null,
    aplicada_en:       (row.aplicada_en as string | null) ?? null,
    error:             (row.error as string | null) ?? null,
    creado_en:         row.creado_en as string,
    expira_en:         row.expira_en as string,
  };
}

export class SupabaseAgentProposalRepository implements IAgentProposalRepository {
  private readonly client: SupabaseClient;
  constructor(client: SupabaseClient) { this.client = client; }

  async create(nueva: NuevaPropuesta): Promise<AgentProposal> {
    const firmId = await requireAuth();
    const companyId = assertCompanyId(nueva.company_id, 'AgentProposalRepository.create');
    if (!Number.isFinite(nueva.confianza) || nueva.confianza < 0 || nueva.confianza > 1) {
      throw new Error('[AgentProposal.create] confianza fuera de 0–1');
    }
    // Solo columnas de la propuesta: estado, revisión y fechas los fija la base de datos.
    const { data, error } = await this.client
      .from(TABLE)
      .insert({
        firm_id:           firmId,
        company_id:        companyId,
        periodo:           nueva.periodo,
        agente:            nueva.agente,
        tipo:              nueva.tipo,
        entidad_id:        nueva.entidad_id,
        propuesta:         nueva.propuesta,
        confianza:         nueva.confianza,
        explicacion:       nueva.explicacion,
        creado_por_agente: nueva.creado_por_agente,
      })
      .select(COLS)
      .single();
    if (error) {
      if ((error as { code?: string }).code === PG_UNIQUE_VIOLATION) throw new PropuestaDuplicadaError();
      throw new Error(`[AgentProposal.create] ${error.message}`);
    }
    return mapRow(data as unknown as Record<string, unknown>);
  }

  async getById(id: string): Promise<AgentProposal | undefined> {
    const firmId = await requireAuth();
    const { data, error } = await this.client
      .from(TABLE)
      .select(COLS)
      .eq('firm_id', firmId)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(`[AgentProposal.getById] ${error.message}`);
    return data ? mapRow(data as unknown as Record<string, unknown>) : undefined;
  }

  async list(
    companyId: string,
    periodo: string,
    filtros?: { estado?: EstadoPropuesta; agente?: AgentePropuesta },
  ): Promise<AgentProposal[]> {
    const firmId = await requireAuth();
    const cid = assertCompanyId(companyId, 'AgentProposalRepository.list');
    let q = this.client
      .from(TABLE)
      .select(COLS)
      .eq('firm_id', firmId)
      .eq('company_id', cid)
      .eq('periodo', periodo);
    if (filtros?.estado) q = q.eq('estado', filtros.estado);
    if (filtros?.agente) q = q.eq('agente', filtros.agente);
    const { data, error } = await q.order('creado_en', { ascending: false });
    if (error) throw new Error(`[AgentProposal.list] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRow);
  }

  async listPendientesDeAplicar(companyId: string): Promise<AgentProposal[]> {
    const firmId = await requireAuth();
    const cid = assertCompanyId(companyId, 'AgentProposalRepository.listPendientesDeAplicar');
    const { data, error } = await this.client
      .from(TABLE)
      .select(COLS)
      .eq('firm_id', firmId)
      .eq('company_id', cid)
      .eq('estado', 'aprobada')
      .is('aplicada_en', null)
      .order('revisado_en', { ascending: true });
    if (error) throw new Error(`[AgentProposal.listPendientesDeAplicar] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRow);
  }

  async transition(
    id: string,
    nuevoEstado: Exclude<EstadoPropuesta, 'pendiente'>,
    valorFinal?: JsonObject,
  ): Promise<AgentProposal> {
    await requireAuth();
    const { data, error } = await this.client.rpc('agent_proposal_transition', {
      p_id: id,
      p_nuevo_estado: nuevoEstado,
      p_valor_final: valorFinal ?? null,
    });
    if (error) throw new Error(`[AgentProposal.transition] ${error.message}`);
    return mapRow(data as unknown as Record<string, unknown>);
  }

  async markApplied(id: string, error?: string): Promise<AgentProposal> {
    await requireAuth();
    const { data, error: rpcError } = await this.client.rpc('agent_proposal_mark_applied', {
      p_id: id,
      p_error: error ?? null,
    });
    if (rpcError) throw new Error(`[AgentProposal.markApplied] ${rpcError.message}`);
    return mapRow(data as unknown as Record<string, unknown>);
  }

  async expireDue(companyId: string): Promise<number> {
    await requireAuth();
    const cid = assertCompanyId(companyId, 'AgentProposalRepository.expireDue');
    const { data, error } = await this.client.rpc('agent_proposal_expire_due', { p_company_id: cid });
    if (error) throw new Error(`[AgentProposal.expireDue] ${error.message}`);
    return Number(data ?? 0);
  }
}
